import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  Tabs,
  useDisclosure
} from '@nextui-org/react';
import {
  collection,
  doc,
  getCountFromServer,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../lib/firebase';
import { apiTry } from '../lib/api';
import { applySort } from '../lib/sort';
import { useAuth } from '../hooks/useAuth';
import { crmText } from '../lib/crmI18n';
import { crmDenied } from '../lib/crmAccess';
import { maskEmail } from '../lib/mask';
import { AgencyShared as S } from '../lib/agencyApp';
import { confirmAndDeleteAgency } from '../lib/agencyActions';
import RegionSelect from '../components/agency/RegionSelect';

const PLAN_RANK = { rookie: 0, silver: 1, gold: 2, titanium: 3 };
const PLAN_CHIP = { rookie: 'default', silver: 'default', gold: 'warning', titanium: 'secondary' };

const PAGE_LIMIT = 2000;

// Agencia de prueba (las de demostración que crea el equipo): la misma marca
// que usa el Worker (cf-worker/src/lib/agencyDirectory.js › isDemoAgency).
const isTestAgency = (a) => a?.testData === true;

const DIRECTORY_ENDPOINT = '/api/backoffice/agency-directory';

// Un 404 es que el Worker que contesta aún no tiene el interruptor (sin desplegar).
function directoryError(res, doing) {
  if (res.status === 404) return `${doing}: el Worker aún no tiene este interruptor. Hay que desplegarlo.`;
  if (res.status === 403) return `${doing}: solo un admin puede cambiarlo.`;
  const detail = typeof res.data === 'string' ? res.data : res.data?.error;
  return `${doing} (${res.status}${detail ? `: ${detail}` : ''}).`;
}

// Créditos del plan que tiene de verdad: catálogo + condiciones a medida
// (planOverrides) + prueba o margen, con el mismo código que la app.
export function agencyCredits(a) {
  return S.agencyPlan(a || {}).maxCredits;
}

// Puerto directo de generateAgencyInviteCode() en admin.js:19.
// Genera un ID determinista a partir del nombre — mismo algoritmo.
function generateAgencyId(name) {
  if (!name || !name.trim()) return '';
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const normalized = name.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  let h = 5381;
  for (let i = 0; i < normalized.length; i++) {
    h = Math.abs((h * 33 + normalized.charCodeAt(i)) % 2147483647);
  }
  let suffix = '';
  for (let i = 0; i < 6; i++) {
    suffix += chars[h % chars.length];
    h = Math.abs((h * 17 + 13) % 2147483647);
  }
  return normalized.slice(0, 10) + '-' + suffix;
}

const EMPTY_FORM = {
  name: '',
  plan: 'silver',
  region: '',
  validated: false
};

function AgencyLogo({ a }) {
  if (/^https?:\/\//i.test(a.logoUrl || '')) {
    return <img src={a.logoUrl} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />;
  }
  const initial = String(a.name || a.id || '?').trim()[0]?.toUpperCase() || '?';
  return <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-default-200 text-sm font-semibold">{initial}</span>;
}

export default function Agencias() {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [validFilter, setValidFilter] = useState('all');
  // Reales o de prueba (testData: true).
  const [kindFilter, setKindFilter] = useState('all');
  const [toast, setToast] = useState(null);
  const [sortDescriptor, setSortDescriptor] = useState({
    column: 'name',
    direction: 'ascending'
  });
  const navigate = useNavigate();
  const { canWrite, isViewer, user } = useAuth();

  const form = useDisclosure();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Solicitudes nuevas de «¿Tu agencia no aparece? Aplica aquí» (contador del botón).
  const [newApplications, setNewApplications] = useState(null);
  useEffect(() => {
    getCountFromServer(query(collection(db, 'agency_applications'), where('status', '==', 'new')))
      .then((snap) => setNewApplications(snap.data().count))
      .catch(() => setNewApplications(null));
  }, []);

  // Avisos nuevos «ha gastado el 80 % de sus créditos» (los crea el Worker).
  const [newCreditAlerts, setNewCreditAlerts] = useState(null);
  useEffect(() => {
    getCountFromServer(query(collection(db, 'agency_credit_alerts'), where('status', '==', 'new')))
      .then((snap) => setNewCreditAlerts(snap.data().count))
      .catch(() => setNewCreditAlerts(null));
  }, []);

  // «Ocultar las agencias de prueba del Explorador» (testData: true). Lo guarda
  // el Worker (cf-worker/src/api/agencyDirectory.js, D1 app_config['agency_directory']).
  const [dirConfig, setDirConfig] = useState(null); // { hideTest } | null mientras carga
  const [dirSaving, setDirSaving] = useState(false);
  const [dirError, setDirError] = useState(null);
  useEffect(() => {
    let alive = true;
    apiTry(DIRECTORY_ENDPOINT).then((res) => {
      if (!alive) return;
      if (res.ok) setDirConfig({ hideTest: res.data?.config?.hideTest === true });
      else setDirError(directoryError(res, 'No se pudo leer si se ocultan'));
    }).catch((e) => { if (alive) setDirError(`No se pudo leer si se ocultan (${e.message || 'error'}).`); });
    return () => { alive = false; };
  }, []);

  async function setHideTest(hideTest) {
    const before = dirConfig;
    setDirConfig({ hideTest });
    setDirSaving(true);
    setDirError(null);
    try {
      const res = await apiTry(DIRECTORY_ENDPOINT, { method: 'PUT', body: { config: { hideTest } } });
      if (!res.ok) throw new Error(directoryError(res, 'No se ha guardado'));
      setDirConfig({ hideTest: res.data?.config?.hideTest === true });
      setToast({
        type: 'ok',
        msg: hideTest
          ? 'Agencias de prueba ocultas: en menos de un minuto el Explorador solo enseña las reales.'
          : 'Agencias de prueba visibles otra vez: en menos de un minuto vuelven al Explorador.'
      });
    } catch (e) {
      setDirConfig(before);
      setDirError(e.message || 'No se ha guardado.');
    } finally {
      setDirSaving(false);
    }
  }

  // Suscripción en tiempo real a agencies.
  useEffect(() => {
    setLoading(true);
    setError(null);
    const q = query(
      collection(db, 'agencies'),
      orderBy('name'),
      fsLimit(PAGE_LIMIT)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setAgencies(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error('[Agencias] onSnapshot error:', err);
        setError(err.message || 'Error cargando agencias');
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  // El plan que tiene cada una de verdad (prueba, margen y condiciones a medida).
  const plans = useMemo(() => {
    const out = new Map();
    for (const a of agencies) out.set(a.id, S.agencyPlan(a));
    return out;
  }, [agencies]);

  const counts = useMemo(() => {
    const acc = {
      all: agencies.length,
      rookie: 0,
      silver: 0,
      gold: 0,
      titanium: 0,
      other: 0,
      validated: 0,
      pending: 0,
      test: 0,
      testListed: 0
    };
    for (const a of agencies) {
      const p = a.plan || 'silver';
      if (acc[p] !== undefined) acc[p]++;
      else acc.other++;
      if (a.validated) acc.validated++;
      else acc.pending++;
      if (isTestAgency(a)) {
        acc.test++;
        // Las que el Explorador enseñaría (validadas y publicadas; el plan lo mira el Worker).
        if (a.validated === true && a.published !== false) acc.testListed++;
      }
    }
    return acc;
  }, [agencies]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const base = agencies.filter((a) => {
      const p = a.plan || 'silver';
      if (planFilter === 'other' ? PLAN_RANK[p] !== undefined : planFilter !== 'all' && p !== planFilter) return false;
      if (validFilter === 'validated' && !a.validated) return false;
      if (validFilter === 'pending' && a.validated) return false;
      if (kindFilter === 'real' && isTestAgency(a)) return false;
      if (kindFilter === 'test' && !isTestAgency(a)) return false;
      if (!needle) return true;
      return [a.name, a.id, a.region, a.ownerEmail, a.contactEmail]
        .some((v) => String(v || '').toLowerCase().includes(needle));
    });

    return applySort(base, sortDescriptor, {
      name: (a) => (a.name || '').toLowerCase(),
      plan: (a) => PLAN_RANK[plans.get(a.id)?.id || a.plan || 'silver'] ?? -1,
      region: (a) => (a.region || '').toLowerCase(),
      owner: (a) => (a.ownerEmail || '').toLowerCase(),
      credits: (a) => agencyCredits(a),
      validated: (a) => (a.validated === true ? 0 : 1)
    });
  }, [agencies, plans, search, planFilter, validFilter, kindFilter, sortDescriptor]);

  function openNew() {
    setFormData(EMPTY_FORM);
    form.onOpen();
  }

  async function saveForm() {
    if (!formData.name.trim()) {
      setToast({ type: 'error', msg: 'El nombre es obligatorio.' });
      return;
    }
    setSaving(true);
    try {
      const now = serverTimestamp();
      // ID determinista a partir del nombre
      const newId = generateAgencyId(formData.name);
      if (!newId) {
        throw new Error('No se pudo generar un ID para el nombre dado.');
      }
      if (agencies.some((a) => a.id === newId)) {
        throw new Error(`Ya existe una agencia con ID ${newId}. Cambia el nombre.`);
      }
      await setDoc(doc(db, 'agencies', newId), {
        name: formData.name.trim(),
        plan: formData.plan,
        region: formData.region || '',
        validated: formData.validated,
        updatedAt: now,
        createdAt: now,
        createdBy: auth.currentUser?.uid || null,
        // Nace sin publicar: la publica su Owner desde la consola cuando la
        // tenga lista. Hasta entonces no la ven sus creadores.
        published: false,
        validatedAt: formData.validated ? now : null,
        validatedBy: formData.validated ? auth.currentUser?.uid : null
      });
      form.onClose();
      // Lo siguiente es ponerle dueño y condiciones: en su página.
      navigate(`/agencias/${encodeURIComponent(newId)}`);
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  }

  // Bienvenida al Owner («Tu agencia ya está activa en TikControl»): al
  // validar, el Worker se la manda a su Owner si lo tiene y aún no la recibió
  // (una vez por Owner). Devuelve el texto que se añade al aviso, o ''.
  async function ownerWelcome(agencyId) {
    try {
      const res = await apiTry('/api/agency/owner-welcome', { method: 'POST', body: { agencyId } });
      return res.ok && res.data?.sent > 0 ? ' Correo de bienvenida enviado al Owner.' : '';
    } catch (_) {
      return '';
    }
  }

  async function toggleValidated(a) {
    const next = !a.validated;
    try {
      await updateDoc(doc(db, 'agencies', a.id), {
        validated: next,
        validatedAt: next ? serverTimestamp() : null,
        validatedBy: next ? auth.currentUser?.uid || null : null,
        updatedAt: serverTimestamp()
      });
      setAgencies((list) =>
        list.map((x) => (x.id === a.id ? { ...x, validated: next } : x))
      );
      const welcomed = next ? await ownerWelcome(a.id) : '';
      setToast({
        type: 'ok',
        msg: next ? `Agencia validada.${welcomed}` : 'Agencia invalidada.'
      });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    }
  }

  // Borrado en cascada: el ID de agencia se escribe a mano y se reutiliza, así
  // que dejar los datos colgando hacía que una agencia nueva con el mismo ID
  // heredara creadores, misiones, eventos y roles de la anterior.
  async function handleDelete(a) {
    try {
      const res = await confirmAndDeleteAgency(a);
      if (!res) return;
      if (res.deleted) setAgencies((list) => list.filter((x) => x.id !== a.id));
      if (!res.ok) console.warn('[Agencias] borrado:', res.msg);
      setToast({ type: res.ok ? 'ok' : 'error', msg: res.msg });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    }
  }

  const open = (a) => navigate(`/agencias/${encodeURIComponent(a.id)}`);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Agencias</h1>
          <div className="flex items-center gap-2 text-sm text-default-500">
            {loading ? (
              'Cargando…'
            ) : (
              <>
                <span>
                  {filtered.length} de {agencies.length} (agencies)
                </span>
                {agencies.length >= PAGE_LIMIT && (
                  <Chip size="sm" color="warning" variant="flat">
                    límite {PAGE_LIMIT} alcanzado
                  </Chip>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {!crmDenied(user?.uid) && (
            <Button variant="flat" onPress={() => navigate('/agencias/crm')}>
              {crmText.title}
            </Button>
          )}
          <Button variant="flat" onPress={() => navigate('/agencias/lanzamiento')}>
            Lanzamiento
          </Button>
          <Button variant="flat" onPress={() => navigate('/agencias/live-fest')}>
            LIVE FEST
          </Button>
          <Button variant="flat" onPress={() => navigate('/agencias/avisos-creditos')}>
            Avisos de créditos
            {newCreditAlerts > 0 && <Chip size="sm" color="warning">{newCreditAlerts}</Chip>}
          </Button>
          <Button variant="flat" onPress={() => navigate('/agencias/preventa')}>
            Preventa
          </Button>
          <Button variant="flat" onPress={() => navigate('/agencias/restos')}>
            Restos de borradas
          </Button>
          <Button variant="flat" onPress={() => navigate('/agencias/solicitudes')}>
            Solicitudes
            {newApplications > 0 && <Chip size="sm" color="primary">{newApplications}</Chip>}
          </Button>
          {canWrite && (
            <Button color="primary" onPress={openNew}>
              + Nueva agencia
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-default-200 bg-content1 px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">Ocultar las agencias de prueba del Explorador</p>
          <p className="text-xs text-default-500">
            {loading
              ? 'Contando las agencias de prueba…'
              : `${counts.test} agencias de prueba (marcadas como datos de prueba)` +
                (counts.testListed !== counts.test ? `, ${counts.testListed} validadas y publicadas` : '') + '. '}
            {dirConfig == null
              ? ''
              : dirConfig.hideTest
                ? 'Ahora no salen: el Explorador de la app solo enseña las agencias reales y la ficha de una de prueba no se abre.'
                : 'Ahora salen en el Explorador de la app junto a las reales.'}
            {' '}El cambio tarda hasta un minuto en verse.
          </p>
          {dirError && <p className="text-xs text-danger-600">{dirError}</p>}
        </div>
        <Switch
          isSelected={dirConfig?.hideTest === true}
          isDisabled={!canWrite || dirConfig == null || dirSaving}
          onValueChange={setHideTest}
          aria-label="Ocultar las agencias de prueba del Explorador"
        >
          {dirSaving ? 'Guardando…' : dirConfig?.hideTest ? 'Ocultas' : 'Visibles'}
        </Switch>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Buscar por nombre, ID, región o email…"
          value={search}
          onValueChange={setSearch}
          isClearable
          size="sm"
          className="max-w-sm"
        />
        <Tabs
          size="sm"
          selectedKey={planFilter}
          onSelectionChange={(k) => setPlanFilter(String(k))}
          aria-label="Filtrar por plan contratado"
        >
          <Tab key="all" title={`Todos (${counts.all})`} />
          <Tab key="rookie" title={`Rookie (${counts.rookie})`} />
          <Tab key="silver" title={`Silver (${counts.silver})`} />
          <Tab key="gold" title={`Gold (${counts.gold})`} />
          <Tab key="titanium" title={`Titanium (${counts.titanium})`} />
          {counts.other > 0 && <Tab key="other" title={`Otros (${counts.other})`} />}
        </Tabs>
        <Tabs
          size="sm"
          selectedKey={validFilter}
          onSelectionChange={(k) => setValidFilter(String(k))}
          aria-label="Filtrar por validación"
        >
          <Tab key="all" title="Todos" />
          <Tab key="validated" title={`Validadas (${counts.validated})`} />
          <Tab key="pending" title={`Pendientes (${counts.pending})`} />
        </Tabs>
        <Tabs
          size="sm"
          selectedKey={kindFilter}
          onSelectionChange={(k) => setKindFilter(String(k))}
          aria-label="Filtrar reales o de prueba"
        >
          <Tab key="all" title="Todas" />
          <Tab key="real" title={`Reales (${counts.all - counts.test})`} />
          <Tab key="test" title={`TEST (${counts.test})`} />
        </Tabs>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">
          {error}
        </div>
      )}

      {toast && (
        <div
          className={`rounded-md border p-3 text-sm ${
            toast.type === 'ok'
              ? 'border-success-200 bg-success-50 text-success-700'
              : 'border-danger-200 bg-danger-50 text-danger-700'
          }`}
        >
          <div className="flex items-center justify-between gap-3">
            <span>{toast.msg}</span>
            <button className="text-xs underline" onClick={() => setToast(null)}>
              cerrar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center p-8">
          <Spinner />
        </div>
      ) : (
        <Table
          aria-label="Tabla de agencias"
          isStriped
          removeWrapper
          classNames={{ th: 'bg-default-100' }}
          sortDescriptor={sortDescriptor}
          onSortChange={setSortDescriptor}
        >
          <TableHeader>
            <TableColumn key="name" allowsSorting>Agencia</TableColumn>
            <TableColumn key="plan" allowsSorting>Plan</TableColumn>
            <TableColumn key="region" allowsSorting>Región</TableColumn>
            <TableColumn key="owner" allowsSorting>Dueño</TableColumn>
            <TableColumn key="credits" allowsSorting>Créditos</TableColumn>
            <TableColumn key="validated" allowsSorting>Estado</TableColumn>
            <TableColumn align="end">Acciones</TableColumn>
          </TableHeader>
          <TableBody emptyContent="No hay agencias.">
            {filtered.map((a) => {
              const eff = plans.get(a.id) || S.agencyPlan(a);
              return (
                <TableRow key={a.id}>
                  <TableCell>
                    <button type="button" className="flex items-center gap-2 text-left" onClick={() => open(a)}>
                      <AgencyLogo a={a} />
                      <div className="flex flex-col">
                        <span className="flex items-center gap-1 font-medium hover:underline">
                          {a.name || '—'}
                          {a.verified === true && <span className="text-xs text-primary" title="Verificada">✓</span>}
                          {isTestAgency(a) && (
                            <Chip size="sm" variant="flat" color="secondary">
                              TEST
                            </Chip>
                          )}
                        </span>
                        <span className="text-xs text-default-400">{a.id}</span>
                      </div>
                    </button>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-0.5">
                      <Chip size="sm" variant="flat" color={PLAN_CHIP[eff.id] || 'default'}>
                        {eff.label}
                      </Chip>
                      {eff.trial && <span className="text-xs text-primary">Prueba · vuelve a {S.PLANS[a.plan]?.label || a.plan}</span>}
                      {eff.grace && <span className="text-xs text-warning-600">Margen de ajuste</span>}
                    </div>
                  </TableCell>
                  <TableCell>{a.region || '—'}</TableCell>
                  <TableCell>
                    {a.ownerEmail
                      ? <span className="text-sm">{isViewer ? maskEmail(a.ownerEmail) : a.ownerEmail}</span>
                      : <span className="text-xs text-warning-600">{a.ownerId ? 'Con dueño' : 'Sin dueño'}</span>}
                  </TableCell>
                  <TableCell>{S.limitLabel(eff.maxCredits)}</TableCell>
                  <TableCell>
                    {a.validated ? (
                      <Chip size="sm" color="success" variant="flat">
                        Validada
                      </Chip>
                    ) : (
                      <Chip size="sm" color="warning" variant="flat">
                        Pendiente
                      </Chip>
                    )}
                    {/* Sin publicar por su Owner: sus creadores aún no la ven. La publica
                        él desde la consola (Agencia), una sola vez: aquí solo se ve. */}
                    {a.published === false && (
                      <Chip size="sm" color="default" variant="flat" className="ml-1">
                        Sin publicar
                      </Chip>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="flat" color="primary" onPress={() => open(a)}>
                        Abrir
                      </Button>
                      {canWrite && (
                        <>
                          <Button
                            size="sm"
                            variant="flat"
                            color={a.validated ? 'warning' : 'success'}
                            onPress={() => toggleValidated(a)}
                          >
                            {a.validated ? 'Invalidar' : 'Validar'}
                          </Button>
                          <Dropdown>
                            <DropdownTrigger>
                              <Button size="sm" variant="light" isIconOnly aria-label={`Más acciones de ${a.name || a.id}`}>⋯</Button>
                            </DropdownTrigger>
                            <DropdownMenu aria-label="Más acciones" onAction={(key) => { if (key === 'delete') handleDelete(a); }}>
                              <DropdownItem key="delete" className="text-danger" color="danger">Eliminar agencia</DropdownItem>
                            </DropdownMenu>
                          </Dropdown>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Nueva agencia: lo justo para crearla; dueño, ficha y condiciones,
          en su página. */}
      <Modal
        isOpen={form.isOpen}
        onClose={form.onClose}
        size="xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          <ModalHeader>Nueva agencia</ModalHeader>
          <ModalBody className="flex flex-col gap-3">
            <Input
              label="Nombre"
              value={formData.name}
              onValueChange={(v) => setFormData((f) => ({ ...f, name: v }))}
              isRequired
              description={`ID previsto: ${generateAgencyId(formData.name) || '—'}`}
            />
            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Plan"
                selectedKeys={[formData.plan]}
                onSelectionChange={(keys) => {
                  const plan = String(Array.from(keys)[0] || 'silver');
                  setFormData((f) => ({ ...f, plan }));
                }}
                description={`Créditos del plan: ${S.PLANS[formData.plan]?.maxCredits ?? '—'}`}
              >
                {(S.PLAN_ORDER || []).map((id) => (
                  <SelectItem key={id}>{S.PLANS[id].label}</SelectItem>
                ))}
              </Select>
              <RegionSelect
                size="md"
                value={formData.region}
                onChange={(v) => setFormData((f) => ({ ...f, region: v }))}
              />
            </div>
            <Switch
              isSelected={formData.validated}
              onValueChange={(v) => setFormData((f) => ({ ...f, validated: v }))}
            >
              Validada
            </Switch>
            <p className="text-xs text-default-500">
              Al crearla se abre su página para ponerle dueño, ficha y condiciones.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={form.onClose} isDisabled={saving}>
              Cancelar
            </Button>
            <Button color="primary" onPress={saveForm} isLoading={saving}>
              Crear
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
